<script lang="ts">
	import { beforeUpdate } from 'svelte';
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Services and configs
	import { devices_config } from 'src/configs/devices';

	// Helpers
	import {
		setupGen1TimerLogic,
		createGen1StatusLabels,
		createGen1CombinedLabelList,
		getGen1DeviceStatuses,
	} from '../../gen1-main-helpers';

	// Design System Components
	import { Alert } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';

	// Components
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';

	// Types
	import type { Device } from 'src/types/Device.ts';
	import { defaultTempFavPositions } from 'src/helpers/interface_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import ControlTrvDevicePageV2 from 'src/components/Controls/Single/ControlTrvDevicePageV2.svelte';
	import ControlTrvBoostV2 from 'src/components/Controls/Single/ControlTrvBoostV2.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	/**
	 * Main control tab for the SHTRV-01 (Gen1 TRV thermostat) sidepanel drawer.
	 *
	 * Responsibilities:
	 * - aggregates live device status/settings and surfaces secondary labels
	 * - displays active Gen1 timers and remaining time when available
	 * - renders TRV temperature control (ControlTrvDevicePage) and boost button (ControlTrvBoostV2)
	 *
	 * @prop device Active device selected in the sidepanel drawer. Expected to be a Gen1 TRV.
	 */
	export let device: Device;

	$: deviceConfig = devices_config?.[device?.type];
	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses, device);
	$: combinedLabelList = createGen1CombinedLabelList(timerLabels, labelList);
	$: currentTemperature = labelList.find((el) => el.name === 'temperature')?.value;

	let timerLabels: Record<string, any> = {};
	let gen1Timer = false;
	let tempUnit = settings?.thermostats?.[0]?.target_t?.units;
	let temp_fav_positions = device?.temp_fav_positions || defaultTempFavPositions(tempUnit);

	beforeUpdate(() => {
		if (
			device &&
			status !== undefined &&
			settings !== undefined &&
			deviceConfig !== undefined &&
			status?.online
		) {
			gen1Timer = setupGen1TimerLogic(device, status, false);
		}
	});
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if gen1Timer}
			{#key status[device.category + 's'][device.channel].timer_started}
				<TimerRemaining timerKey="gen1_main" {device} {status} {settings} bind:timerLabels />
			{/key}
		{/if}
		<DeviceLabelList labelList={combinedLabelList} />
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>
	<div slot="content">
		{#if !device?.shared && temp_fav_positions?.filter((pos) => pos.active).length == 0}
			<Alert
				dismissable={true}
				type="info"
				isHeadingBold
				heading={$format('settings_title_favorites', {
					default: 'Favorite positions',
				})}
				message={$format('alert_info_heading_temp_fav_positions', {
					default:
						'You can enable or disable temperature favorite positions from sensor settings',
				})}
				cta={{
					title: $format('button_enable_disable', {
						default: 'Enable/Disable',
					}),
				}}
				on:cta_click={() => {
					navigateToSetting(
						device.id,
						'sensor_settings',
						'temperature_favorite_positions',
						false,
					);
				}}
			/>
		{/if}
	</div>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<div class="mt-s">
			<ControlTrvDevicePageV2 {device} {status} {settings} current={currentTemperature} />
			<ControlTrvBoostV2 {device} {status} {settings} />
		</div>
	</div>
</DeviceViewStateWrapper>
